import { useEffect, useMemo, useRef, useState, useContext } from 'react';
import { ChevronUp, ChevronDown, Clock, Play, Users, Percent, Activity } from 'react-feather';
import { useHistory, useLocation } from 'react-router';
import moment from 'moment';
import { useRemote } from '../../../Utils/Utils';
import { PAGES, ROUTE_PATHS, BannerContext } from '../../../InternalApiApp';
import { analyticsExportFilename } from '../AnalyticsUtils';
import Page, { PageContentBodyGrid } from '../../Shared/Page/Page';
import PageGridDivider from '../../Shared/Page/PageGridDivider'
import SearchTermFilter from '../../../components/Input/SearchTermFilter';
import PageGridItem from '../../Shared/Page/PageGridItem';
import Table from '../../../components/Table/Table';
import FlexWrapper from '../../FlexWrapper'
import LoadingIcon from '../../Shared/LoadingIcon'
import Tooltip from '../../../components/popups/Tooltip';
import AnalyticsSourceSelector from "../AnalyticsSourceSelector";
import AnalyticsExportButton from '../Components/AnalyticsExportButton';
import DateRangeInput from '../../CraigsLibraryPage/DateRangeInput';
import {
    parseRangeFromSearch, defaultRange, buildRangeSearch, selectedProductParams,
    buildAnalyticsQuery, secondsToHoursLabel, secondsToMmSs, formatPercent,
    tableExportData, summaryExportData, fundLabel, typeLabel, publishedSortKey,
    publishedDateLabel, textComparator, playsTooltip, uniqueViewersTooltip, isListedVideo,
} from './videoAnalyticsUtils';

// Analytics → Videos (CFS-96): Bunny Stream engagement for the video library, date-filtered via
// the banner range picker (?from&to in the URL so the range survives list↔detail navigation) and
// narrowed by the banner product filter. Rows are published videos, About-the-Manager videos and
// "general" videos such as What's Topical (CFS-108); the first column is Portfolio/Manager.
const AnalyticsVideosList = () => {
    const { setBannerBreadCrumbs, setBannerAdditionalContent, setBacNoDivider, bannerAdditionalInfo } = useContext(BannerContext);
    const remote = useRemote();
    const history = useHistory();
    const location = useLocation();

    const urlRange = parseRangeFromSearch(location.search);
    const initialRange = urlRange.from && urlRange.to ? urlRange : defaultRange();

    const [hasSearchTerm, setHasSearchTerm] = useState("")
    const [filteredData, setFilteredData] = useState([])
    const [loading, setLoading] = useState(true)
    const [payload, setPayload] = useState(null)
    const [range, setRange] = useState(initialRange)
    const [sortingColumn, setSortingColumn] = useState({ column: "Plays", ascending: false })

    // The banner content is mounted once; the picker's onChange reaches current state via refs.
    const rangeRef = useRef(range);
    rangeRef.current = range;
    const locationRef = useRef(location);
    locationRef.current = location;

    useEffect(() => {
        const handleRangeChange = ({ from, to }) => {
            const nextFrom = from ? from.format("YYYY-MM-DD") : null;
            const nextTo = to ? to.format("YYYY-MM-DD") : null;
            if (!nextFrom || !nextTo) return; // mid-selection or cleared — wait for a full range
            if (rangeRef.current?.from === nextFrom && rangeRef.current?.to === nextTo) return;
            setRange({ from: nextFrom, to: nextTo });
            history.replace(locationRef.current.pathname + buildRangeSearch(locationRef.current.search, nextFrom, nextTo));
        };

        setBannerBreadCrumbs({ tab: 'Videos' })
        setBacNoDivider(true)
        setBannerAdditionalContent(
            <div style={{ display: "flex", alignItems: "center", justifyContent: "right", marginLeft: "auto", gap: "1rem" }}>
                <DateRangeInput
                    onChange={handleRangeChange}
                    value={{ from: moment(initialRange.from), to: moment(initialRange.to) }}
                />
                <AnalyticsSourceSelector />
            </div>
        )
        return () => {
            setBacNoDivider(false)
            setBannerAdditionalContent(null)
        }
    }, [])

    useEffect(() => {
        // The product filter arrives asynchronously from /analytics/filters (BannerContext starts it
        // as null). On a cold load, fetching before it lands fired a second, identical request a moment
        // later whose predecessor was thrown away — and each one costs the Bunny library listing plus
        // the per-video statistics fan-out. Wait for the filter instead; AnalyticsSourceSelector always
        // resolves it (saved selection, or an empty platform list on failure), so this cannot stall.
        // Arriving from another Analytics page, the filter is already set, so the first fetch goes out
        // at once and the selector's refresh of /analytics/filters may still refire this effect; the
        // stale guard below keeps that harmless, and the server-side caches make the repeat cheap.
        if (!bannerAdditionalInfo) return undefined;
        let stale = false; // a filter change must not be clobbered by a slower earlier response
        setLoading(true)
        const query = buildAnalyticsQuery(range, selectedProductParams(bannerAdditionalInfo));
        remote.get(`analytics/video-metrics${query}`).then((response) => {
            if (stale) return;
            setPayload(response)
            setLoading(false)
        }).catch(() => {
            if (stale) return;
            setPayload({ summary: null, videos: [], dateRanged: true })
            setLoading(false)
        })
        return () => { stale = true; };
    }, [remote, range, bannerAdditionalInfo])

    // stable identity — SearchTermFilter re-filters (and setStates back into us) whenever the
    // dataSet identity changes, so a fresh [] per render would loop. Null published-document
    // fields (unpublished videos) are blanked so the filter doesn't index the literal text
    // "undefined", and the fund/date column labels are precomputed so search matches what's rendered.
    const videos = useMemo(() => (payload?.videos || []).filter(isListedVideo).map(v => ({
        ...v,
        ticker: v.ticker || '',
        fundName: v.fundName || '',
        templateName: v.templateName || '',
        managerName: v.managerName || '',
        generalCategory: v.generalCategory || '',
        generalTitle: v.generalTitle || '',
        fundLabel: fundLabel(v),
        typeLabel: typeLabel(v) || '',
        publishedSortKey: publishedSortKey(v),
        publishedDateLabel: publishedDateLabel(v),
    })), [payload])
    const summary = payload?.summary

    const toggleSort = (column) => {
        const ascending = sortingColumn.column === column ? !sortingColumn.ascending : false
        setSortingColumn({ column, ascending })
    }

    const sortTable = (dataToSort) => {
        if (!dataToSort) return dataToSort
        const tableData = [...dataToSort]
        const asc = sortingColumn.ascending
        const numeric = (get) => (a, b) => asc ? (get(a) || 0) - (get(b) || 0) : (get(b) || 0) - (get(a) || 0)
        switch (sortingColumn.column) {
            case "Fund":
                tableData.sort(textComparator(r => r.fundLabel, asc))
                break;
            case "Template":
                tableData.sort(textComparator(r => r.typeLabel, asc))
                break;
            case "Published":
                // raw ISO yyyy-MM-dd (published date, or upload date for manager videos) —
                // lexicographic order is chronological
                tableData.sort(textComparator(r => r.publishedSortKey, asc))
                break;
            case "Plays":
                tableData.sort(numeric(r => r.plays))
                break;
            case "UniqueViewers":
                tableData.sort(numeric(r => r.uniqueViewers))
                break;
            case "AvgWatchTime":
                tableData.sort(numeric(r => r.avgWatchTimeSeconds))
                break;
            case "PercentWatched":
                tableData.sort(numeric(r => r.percentWatched))
                break;
            case "Engagement":
                tableData.sort(numeric(r => r.engagementScore))
                break;
            default:
                break;
        }
        return tableData
    }

    const sortingHeader = (column, label, tooltip, icon) => ({
        className: icon ? "text-align-right" : undefined,
        text: (
            <Tooltip label={tooltip} disabled={!tooltip} maxWidth={300}>
                <div className='sorting-header' onClick={() => toggleSort(column)}>
                    {icon || label}
                    {sortingColumn.column === column && sortingColumn.ascending === true ?
                        <ChevronUp className="colour-text-primary" size={12} /> :
                        <ChevronDown className={sortingColumn.column === column ? "colour-text-primary" : "colour-text-neutral-dark-30"} size={12} />
                    }
                </div>
            </Tooltip>
        )
    })

    const colWidths = ["auto", "150px", "115px", "110px", "130px", "150px", "120px", "120px"];
    const headers = [
        sortingHeader("Fund", "Portfolio/Manager"),
        sortingHeader("Template", "Type"),
        sortingHeader("Published", "Published"),
        sortingHeader("Plays", null, playsTooltip(payload?.dateRanged, payload?.from), <Play className="colour-text-primary" size={16} />),
        sortingHeader("UniqueViewers", null, uniqueViewersTooltip(payload?.capturedFrom), <Users className="colour-text-primary" size={16} />),
        sortingHeader("AvgWatchTime", null, "Avg Watch Time", <Clock className="colour-text-primary" size={16} />),
        sortingHeader("PercentWatched", null, "% Watched", <Percent className="colour-text-primary" size={16} />),
        sortingHeader("Engagement", null, "Engagement Score", <Activity className="colour-text-primary" size={16} />),
    ]

    const dataMap = (data) => data.map((video, idx) => (
        <tr key={video.guid || idx} className="cursor-pointer" onClick={() => history.push(`${ROUTE_PATHS[PAGES.ANALYTICS]}/videos/${video.guid}${buildRangeSearch("", range?.from, range?.to)}`)}>
            <td>
                <Tooltip label={video.bunnyTitle} disabled={!video.bunnyTitle || video.fundLabel === video.bunnyTitle} maxWidth={300}>
                    <div>{video.fundLabel}</div>
                </Tooltip>
            </td>
            <td><div>{video.typeLabel || "—"}</div></td>
            <td><div>{video.publishedDateLabel}</div></td>
            <td><div className="text-align-right">{video.plays || 0}</div></td>
            <td><div className="text-align-right">{video.uniqueViewers || 0}</div></td>
            <td><div className="text-align-right">{secondsToMmSs(video.avgWatchTimeSeconds)}</div></td>
            <td><div className="text-align-right">{formatPercent(video.percentWatched)}</div></td>
            <td><div className="text-align-right">{video.engagementScore == null ? "—" : video.engagementScore}</div></td>
        </tr>
    ));

    const dataDisplaying = hasSearchTerm ? filteredData : videos
    const sortedDataToDisplay = sortTable(dataDisplaying)
    const dataToDisplay = sortedDataToDisplay ? dataMap(sortedDataToDisplay) : null

    const exportDate = range ? { from: range.from, to: range.to } : null;
    const summaryExportHeaders = useMemo(() => [
        { label: 'Metric', key: 'metric' },
        { label: 'Value', key: 'value' },
    ], []);
    const tableExportHeaders = useMemo(() => [
        { label: 'Portfolio/Manager', key: 'fund' },
        { label: 'Bunny Title', key: 'bunnyTitle' },
        { label: 'Plays', key: 'plays' },
        { label: 'Unique Viewers', key: 'uniqueViewers' },
        { label: 'Avg Watch Time (seconds)', key: 'avgWatchTimeSeconds' },
        { label: '% Watched', key: 'percentWatched' },
        { label: 'Engagement Score', key: 'engagementScore' },
        { label: 'Ticker', key: 'ticker' },
        { label: 'Manager', key: 'manager' },
        { label: 'Type', key: 'template' },
        { label: 'Published Date', key: 'publishedDate' },
        { label: 'Video ID', key: 'videoGuid' },
    ], []);

    return (
        <Page noBanner>
            <PageContentBodyGrid rowGap="xl">
                <PageGridItem colSpan="12">
                    <FlexWrapper fullWidth justify="space-between" style={{ paddingBottom: "1rem" }}>
                        <FlexWrapper gap="xl">
                            <Tooltip disabled={!payload} maxWidth={300} label={playsTooltip(payload?.dateRanged, payload?.from)}>
                                <FlexWrapper gap="xs" direction="column" align="flex-start" >
                                    <label className='total-metrics-label'>Total Plays</label>
                                    {loading ? <LoadingIcon size={32} /> : <h2 className='total-metrics-value'>{summary?.totalPlays ?? 0}</h2>}
                                </FlexWrapper>
                            </Tooltip>
                            <Tooltip disabled={!payload} maxWidth={300} label={uniqueViewersTooltip(payload?.capturedFrom)}>
                                <FlexWrapper gap="xs" direction="column" align="flex-start" >
                                    <label className='total-metrics-label'>Unique Viewers</label>
                                    {loading ? <LoadingIcon size={32} /> : <h2 className='total-metrics-value'>{summary?.uniqueViewers ?? 0}</h2>}
                                </FlexWrapper>
                            </Tooltip>
                            <FlexWrapper gap="xs" direction="column" align="flex-start" >
                                <label className='total-metrics-label'>Total Watch Time</label>
                                {loading ? <LoadingIcon size={32} /> : <h2 className='total-metrics-value'>{secondsToHoursLabel(summary?.totalWatchTimeSeconds)}</h2>}
                            </FlexWrapper>
                            <FlexWrapper gap="xs" direction="column" align="flex-start" >
                                <label className='total-metrics-label'>Avg % Watched</label>
                                {loading ? <LoadingIcon size={32} /> : <h2 className='total-metrics-value'>{formatPercent(summary?.avgPercentWatched)}</h2>}
                            </FlexWrapper>
                            <Tooltip disabled={!summary?.mostViewedName} label={summary && `${summary.mostViewedPlays} plays`}>
                                <FlexWrapper gap="xs" direction="column" align="flex-start" >
                                    <label className='total-metrics-label'>Most Viewed</label>
                                    {loading ? <LoadingIcon size={32} /> : <h2 className='total-metrics-value' style={{ fontSize: "1.25rem" }}>{summary?.mostViewedName || "—"}</h2>}
                                </FlexWrapper>
                            </Tooltip>
                        </FlexWrapper>
                        <AnalyticsExportButton
                            headers={summaryExportHeaders}
                            data={summaryExportData(summary, payload?.dateRanged)}
                            filename={analyticsExportFilename('videos-summary', exportDate)}
                            loading={loading}
                        />
                    </FlexWrapper>
                    <PageGridDivider />
                    <PageGridItem style={{ paddingTop: "1.5rem" }}>
                        <SearchTermFilter
                            size="small"
                            width="l"
                            placeholder={`Search`}
                            isClearable
                            dataSet={videos}
                            setFilteredData={setFilteredData}
                            path={["fundLabel", "ticker", "managerName", "generalTitle", "typeLabel", "publishedDateLabel", "bunnyTitle"]}
                            hasSearchTerm={(boolean) => setHasSearchTerm(boolean)}
                        />
                    </PageGridItem>
                    <FlexWrapper fullWidth justify="space-between" align="center">
                        <div className="table-title">
                            All Videos
                            {payload && payload.dateRanged === false &&
                                <span className="colour-text-neutral-dark-40" style={{ fontSize: "0.75rem", marginLeft: "0.5rem" }}>
                                    (all-time figures — library too large for date filtering)
                                </span>
                            }
                        </div>
                        <AnalyticsExportButton
                            headers={tableExportHeaders}
                            data={tableExportData(sortedDataToDisplay)}
                            filename={analyticsExportFilename('videos-table', exportDate)}
                            loading={loading}
                        />
                    </FlexWrapper>
                    {loading ? <LoadingIcon centered size={48} /> :
                        <Table
                            minWidth={1100}
                            colWidths={colWidths}
                            headers={headers}
                            dataMap={dataToDisplay}
                            noResults={{ message: "No videos found" }}
                            tableStyle={{ maxWidth: 1400 }}
                        />
                    }
                </PageGridItem>
            </PageContentBodyGrid>
        </Page>
    );
}

export default AnalyticsVideosList;
